import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Gift, Crown } from "lucide-react";
import GoogleLoginButton from "@/components/GoogleLoginButton";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]);

  useEffect(() => {
    api.get("/vip/packages").then((r) => setPackages(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) nav(loc.state?.from || "/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-[80vh] max-w-2xl mx-auto px-6 pt-20 pb-20 fade-up" data-testid="login-page">
      <div className="overline mb-3">{t("auth.login_overline")}</div>
      <h1 className="font-serif text-4xl md:text-5xl font-light mb-8" style={{ color: "var(--text-primary)" }}>
        {t("auth.login_heading_pre")}<em style={{ color: "var(--brand-primary)", fontStyle: "italic" }}>{t("auth.login_heading_em")}</em>
      </h1>

      <div className="max-w-md mb-6" data-testid="login-google-section">
        <GoogleLoginButton label={t("auth.continue_google")} testid="login-google-btn" />
        <div className="flex items-center gap-3 my-4">
          <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
          <span className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--text-muted)" }}>{t("auth.or_with_email")}</span>
          <div style={{ flex: 1, height: 1, background: "var(--border-subtle)" }} />
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col gap-4 max-w-md">
        <input type="email" required placeholder={t("auth.email")} className="input-field" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" />
        <input type="password" required placeholder={t("auth.password")} className="input-field" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
        {error && <p className="text-xs" style={{ color: "#ef6d6d" }} data-testid="login-error">{error}</p>}
        <button type="submit" disabled={loading} className="btn-primary mt-2" data-testid="login-submit">
          {loading ? t("auth.logging_in") : t("auth.login_btn")}
        </button>
      </form>

      <div className="mt-8 text-sm" style={{ color: "var(--text-secondary)" }}>
        {t("auth.no_account_q")} <Link to="/registro" className="hover:underline" style={{ color: "var(--brand-primary)" }}>{t("auth.register_link")}</Link>
      </div>

      {packages.length > 0 && (
        <section className="mt-14" data-testid="login-pricing-section">
          <div
            className="p-5 text-center mb-5"
            style={{
              background: "linear-gradient(135deg, rgba(212,175,55,0.14), rgba(200,16,46,0.12))",
              border: "1px solid var(--vip-gold)",
              borderRadius: "2px",
            }}
            data-testid="login-free-week-banner"
          >
            <Gift size={22} strokeWidth={1.3} color="var(--vip-gold)" className="mx-auto mb-2" />
            <h2 className="font-serif text-xl md:text-2xl mb-1" style={{ color: "var(--vip-gold)" }}>
              {t("auth.free_week_login_title")}
            </h2>
            <p className="text-xs" style={{ color: "var(--text-secondary)", lineHeight: 1.7 }}>
              {t("auth.free_week_login_desc_1")}<br />
              {t("auth.free_week_login_desc_2")}
            </p>
          </div>

          <div className="overline mb-3" style={{ color: "var(--brand-primary)" }}>{t("auth.plans_after_free")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="login-pricing-grid">
            {packages.map((p) => (
              <Link
                key={p.id}
                to={`/vip?plan=${p.id}`}
                className="p-4 text-center transition-all"
                style={{
                  background: "var(--bg-surface)",
                  border: "1px solid var(--border-accent)",
                  borderRadius: "2px",
                  display: "block",
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--brand-primary)";
                  e.currentTarget.style.background = "var(--bg-elevated)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "var(--border-accent)";
                  e.currentTarget.style.background = "var(--bg-surface)";
                }}
                data-testid={`login-pricing-card-${p.id}`}
              >
                <Crown size={12} color="var(--vip-gold)" className="mx-auto mb-1" />
                <div className="text-xs uppercase tracking-[0.2em]" style={{ color: "var(--text-secondary)" }}>
                  {p.label}
                </div>
                <div className="font-serif text-2xl font-light mt-1" style={{ color: "var(--vip-gold)" }}>
                  ${p.amount.toLocaleString("es-MX")}
                </div>
                <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>
                  {p.currency.toUpperCase()} · {p.days} {t("common.days")}
                </p>
                <span
                  className="inline-block mt-3 text-xs uppercase tracking-[0.2em]"
                  style={{ color: "var(--brand-primary)", fontWeight: 600 }}
                >
                  {t("auth.activate")}
                </span>
              </Link>
            ))}
          </div>
          <p className="text-xs mt-5 text-center" style={{ color: "var(--text-muted)" }}>
            <Link to="/vip" className="hover:underline" style={{ color: "var(--brand-primary)" }}>
              {t("auth.view_all_plans")}
            </Link>
          </p>
        </section>
      )}
    </div>
  );
}
